import { Box, Button, Menu, MenuItem, Typography, Stack } from "@mui/material";
import { useState } from "react";
import { KeyboardArrowDown } from "@mui/icons-material";

interface Language {
  code: string;
  name: string;
}

const languages: Language[] = [
  { code: "en", name: "English (United States)" },
  { code: "es", name: "Español" },
  { code: "fr", name: "Français" },
  { code: "de", name: "Deutsch" },
  { code: "it", name: "Italiano" },
  { code: "pt", name: "Português" },
];

interface AuthFooterProps {
  currentLanguage?: string;
  onLanguageChange?: (language: Language) => void;
}

export const AuthFooter = ({
  currentLanguage = "en",
  onLanguageChange,
}: AuthFooterProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (language: Language) => {
    onLanguageChange?.(language);
    handleClose();
  };

  const handleExternalLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      component="footer"
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        py: { xs: 1, sm: 2 },
        px: { xs: 2, sm: 4 },
        backgroundColor: "rgba(25, 34, 49, 0.95)",
        backdropFilter: "blur(10px)",
        borderTop: "1px solid",
        borderColor: "rgba(255, 255, 255, 0.1)",
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          maxWidth: "lg",
          mx: "auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 2 },
        }}
      >
        <Button
          id="language-button"
          aria-controls={open ? "language-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          endIcon={<KeyboardArrowDown />}
          sx={{
            color: "text.secondary",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          {languages.find((lang) => lang.code === currentLanguage)?.name ||
            "English (United States)"}
        </Button>
        <Menu
          id="language-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "language-button",
          }}
          PaperProps={{
            sx: {
              mt: 1,
              backgroundColor: "background.paper",
              borderRadius: 2,
              boxShadow: 4,
            },
          }}
        >
          {languages.map((language) => (
            <MenuItem
              key={language.code}
              onClick={() => handleLanguageSelect(language)}
              selected={language.code === currentLanguage}
              sx={{
                py: 1,
                px: 2,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                "&.Mui-selected": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              {language.name}
            </MenuItem>
          ))}
        </Menu>

        <Stack
          direction="row"
          spacing={{ xs: 2, sm: 3 }}
          sx={{
            "& button": {
              color: "text.secondary",
              textTransform: "none",
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              minWidth: "auto",
              padding: { xs: "2px 8px", sm: "4px 12px" },
              "&:hover": {
                backgroundColor: "action.hover",
                textDecoration: "underline",
              },
            },
          }}
        >
          <Button
            onClick={() => handleExternalLink("https://venustusy.com/contact")}
          >
            Help
          </Button>
          <Button
            onClick={() => handleExternalLink("https://venustusy.com/privacy")}
          >
            Privacy
          </Button>
          <Button
            onClick={() => handleExternalLink("https://venustusy.com/terms")}
          >
            Terms
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
